import * as React from 'react'
import { useEffect, useState } from 'react'
import Select from 'react-select'
import * as Sentry from '@sentry/browser'
import { Button, FormGroup, Icon, Spinner, SpinnerSize } from '@blueprintjs/core'
import { GenericCheckbox, LoadableSelect } from '../../components/Inputs'
import Client from '../../client'
import { ListingStrategy } from '../../types/api'
import { ExpressListPriceInput } from './ExpressListPriceInput'
import { ExpressListDrawerSection } from './ExpressListDrawerSection'
import { StrategyPicker } from './StrategyPicker'
import { SgwForm, EbayForm } from './types'
import { hasEbayChannel, hasShopgoodwillChannel } from './channelHelpers'

export type DrawerSection = 'shipping' | null

interface Props {
  formLoading: boolean
  sku: string | null
  strategies: ListingStrategy[]
  strategyId: number
  onChangeStrategy: (strategyId: number) => void
  firstStepChannelIds: number[]
  sgwForm: SgwForm
  ebayForm: EbayForm
  calculatedShipping: boolean
  editingSection: DrawerSection
  onToggleSection: (section: DrawerSection) => void
  onSgwValueChange: (newValues: Partial<SgwForm>) => void
  onEbayValueChange: (newValues: Partial<EbayForm>) => void
  onBuyNowPriceChange: () => void
  onShippingPriceChange: () => void
  onShippingMethodChange: () => void
  onClose: () => void
  strategyDisabled?: boolean
}

const formatListingType = (sgwForm: SgwForm) => (sgwForm.listingType === 'AUCTION' ? 'Auction' : 'Fixed')

const formatCurrency = (value?: string) => (value ? `$${value}` : '—')

export const ExpressListDrawer: React.FC<Props> = ({
  formLoading,
  sku,
  strategies,
  strategyId,
  onChangeStrategy,
  firstStepChannelIds,
  sgwForm,
  ebayForm,
  calculatedShipping,
  editingSection,
  onToggleSection,
  onSgwValueChange,
  onEbayValueChange,
  onBuyNowPriceChange,
  onShippingPriceChange,
  onShippingMethodChange,
  onClose,
  strategyDisabled = false
}) => {
  const showSgwShipping = hasShopgoodwillChannel(firstStepChannelIds)
  const showEbay = hasEbayChannel(firstStepChannelIds)
  // Only label each market's shipping config when both are shown together, so single-market views stay clean.
  const showMarketLabels = showSgwShipping && showEbay

  // eBay shipping is driven by the seller's business (shipping) policies. Load them so we can show the
  // selected profile's name (not just its id) and let the user override which profile the listing uses.
  const [ebayShippingOptions, setEbayShippingOptions] = useState<{ value: number; label: string }[]>([])
  const ebayMarketAccountId = ebayForm.marketAccountId

  useEffect(() => {
    if (!showEbay || !ebayMarketAccountId) {
      setEbayShippingOptions([])
      return undefined
    }

    let cancelled = false

    const loadShippingProfiles = async () => {
      try {
        const res = await Client.getEbaySellerProfilesV5(ebayMarketAccountId)
        if (cancelled) return
        setEbayShippingOptions(
          (res.data.profiles || [])
            .filter(profile => profile.hidden === false && profile.profileType === 'SHIPPING')
            .map(profile => ({ value: profile.externalId, label: profile.name }))
        )
      } catch (e) {
        if (cancelled) return
        Sentry.captureException(e)
        setEbayShippingOptions([])
      }
    }

    loadShippingProfiles()

    return () => {
      cancelled = true
    }
  }, [showEbay, ebayMarketAccountId])

  const selectedEbayShippingProfileLabel =
    ebayShippingOptions.find(option => option.value === ebayForm.shippingProfileId)?.label ||
    (ebayForm.shippingProfileId ? `Profile #${ebayForm.shippingProfileId}` : 'From strategy defaults')

  // Load carriers so the shipping-method view can show the carrier name (not just "Carrier #<id>")
  // without the user having to open the edit form.
  const [carrierOptions, setCarrierOptions] = useState<{ value: number; label: string }[]>([])

  useEffect(() => {
    if (!showSgwShipping) {
      setCarrierOptions([])
      return undefined
    }

    let cancelled = false

    const loadCarriers = async () => {
      try {
        const res = await Client.carriersIndex()
        if (cancelled) return
        setCarrierOptions(
          (res.data.carriers || []).map((carrier: { id: number; name: string }) => ({
            value: carrier.id,
            label: carrier.name
          }))
        )
      } catch (e) {
        if (cancelled) return
        Sentry.captureException(e)
        setCarrierOptions([])
      }
    }

    loadCarriers()

    return () => {
      cancelled = true
    }
  }, [showSgwShipping])

  const selectedCarrierLabel =
    carrierOptions.find(option => option.value === sgwForm.carrierId)?.label ||
    (sgwForm.carrierId ? `Carrier #${sgwForm.carrierId}` : '—')

  if (formLoading) {
    return <Spinner />
  }

  return (
    <>
      <h4>INVENTORY DETAILS</h4>

      <Button
        minimal
        icon={<Icon size={20} icon="export" />}
        className="close-button"
        data-testid="drawer-close-button"
        onClick={onClose}
      />

      <FormGroup label="SKU:" inline className="sku-field">
        {sku || <Spinner size={SpinnerSize.SMALL} />}
      </FormGroup>

      <hr />

      <h4>LISTING STRATEGY</h4>

      <div data-testid="drawer-strategy-formgroup">
        <FormGroup label="Listing strategy:" inline>
          <StrategyPicker
            maybe={false}
            label={null}
            strategies={strategies}
            strategyId={strategyId}
            setStrategyId={onChangeStrategy}
            disabled={strategyDisabled}
          />
        </FormGroup>
        {showSgwShipping && (
          <FormGroup label="Listing type:" inline>
            {formatListingType(sgwForm)}
          </FormGroup>
        )}
        {sgwForm.buyItNowPrice && (
          <FormGroup label="Buy Now Price:" labelFor="buy-now-price-input" inline>
            <ExpressListPriceInput
              id="buy-now-price-input"
              value={sgwForm.buyItNowPrice}
              nonClearable
              onValueChange={(valueAsString: string) => {
                onSgwValueChange({ buyItNowPrice: valueAsString })
                onBuyNowPriceChange()
              }}
            />
          </FormGroup>
        )}
      </div>

      <hr />

      {(showSgwShipping || showEbay) && (
        <ExpressListDrawerSection
          title="SHIPPING DETAILS"
          editing={editingSection === 'shipping'}
          onToggleEdit={() => onToggleSection(editingSection === 'shipping' ? null : 'shipping')}
          data-testid="drawer-shipping-section"
          viewContent={
            <>
              {showSgwShipping && (
                <>
                  {showMarketLabels && <h5 className="express-list-drawer-market-label">Shopgoodwill</h5>}
                  <FormGroup label="Shipping Price:" inline>
                    {calculatedShipping ? 'Calculated' : formatCurrency(sgwForm.shippingPrice)}
                  </FormGroup>
                  <FormGroup label="Shipping Method:" inline>
                    {selectedCarrierLabel}
                  </FormGroup>
                  <FormGroup label="No combine shipping:" inline>
                    {sgwForm.noCombineShipping ? 'Yes' : 'No'}
                  </FormGroup>
                  <FormGroup label="Featured item:" inline>
                    {sgwForm.itemFeatured ? 'Yes' : 'No'}
                  </FormGroup>
                </>
              )}
              {showEbay && (
                <>
                  {showMarketLabels && <h5 className="express-list-drawer-market-label">eBay</h5>}
                  <FormGroup label="Shipping Profile:" inline>
                    {selectedEbayShippingProfileLabel}
                  </FormGroup>
                </>
              )}
            </>
          }
          editContent={
            <>
              {showSgwShipping && (
                <>
                  {showMarketLabels && <h5 className="express-list-drawer-market-label">Shopgoodwill</h5>}
                  {sgwForm.shippingPrice && (
                    <FormGroup label="Shipping Price:" labelFor="shipping-price-input" inline>
                      <ExpressListPriceInput
                        id="shipping-price-input"
                        value={sgwForm.shippingPrice}
                        nonClearable
                        onValueChange={(valueAsString: string) => {
                          onSgwValueChange({ shippingPrice: valueAsString })
                          onShippingPriceChange()
                        }}
                      />
                    </FormGroup>
                  )}

                  <FormGroup label="Shipping Method:" inline className="no-right-margin">
                    <LoadableSelect
                      className="maybe-dark-select"
                      load={Client.carriersIndex}
                      process={d => d.carriers.map(s => ({ value: s.id, label: s.name }))}
                      onChange={v => {
                        onSgwValueChange({ carrierId: v })
                        onShippingMethodChange()
                      }}
                      value={sgwForm.carrierId}
                      style={{ marginTop: 0 }}
                      autoload
                      noLabel
                    />
                  </FormGroup>

                  <GenericCheckbox
                    title="No Combine Shipping"
                    value={sgwForm.noCombineShipping}
                    onChange={value => {
                      onSgwValueChange({ noCombineShipping: value })
                    }}
                  />
                  <GenericCheckbox
                    title="Featured Item (additional cost: $6.95)"
                    value={sgwForm.itemFeatured}
                    onChange={value => {
                      onSgwValueChange({ itemFeatured: value })
                    }}
                  />
                </>
              )}
              {showEbay && (
                <>
                  {showMarketLabels && <h5 className="express-list-drawer-market-label">eBay</h5>}
                  <FormGroup label="Shipping Profile:" inline className="no-right-margin">
                    <Select
                      className="maybe-dark-select"
                      options={ebayShippingOptions}
                      value={ebayForm.shippingProfileId}
                      simpleValue
                      clearable={false}
                      placeholder="Select..."
                      onChange={(value: number) => onEbayValueChange({ shippingProfileId: value })}
                    />
                  </FormGroup>
                </>
              )}
            </>
          }
        />
      )}
    </>
  )
}
